import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useFavorites } from "../auth/FavoritesContext";
import Shell from "../components/Shell";
import { videoCategories, videoInCategory, categoryKey, HIDDEN_CATALOG_CATEGORIES } from "../categories";
import { buildSeriesShows, isSeriesVideo, showToCatalogItem } from "../series";
import { SagasRow } from "./SagasPage";
import { AnimeSeriesRow, SeriesRow } from "./SeriesPage";
import { slugifyGenre } from "./MoviesPage";

function groupByGenre(items) {
  const map = new Map();
  items.forEach((v) => {
    const cats = videoCategories(v);
    cats.forEach((key) => {
      if (HIDDEN_CATALOG_CATEGORIES.has(categoryKey(key))) return;
      // videoCategories ya devuelve nombres canónicos; clave estable por si acaso.
      if (!map.has(key)) map.set(key, []);
      const bucket = map.get(key);
      const id = v.catalog_key || v.id;
      if (!bucket.some((x) => (x.catalog_key || x.id) === id)) bucket.push(v);
    });
  });
  return [...map.entries()]
    .map(([name, list]) => [
      name,
      [...list].sort(
        (a, b) =>
          (b.id || 0) - (a.id || 0) ||
          String(a.title).localeCompare(String(b.title), "es")
      ),
    ])
    .sort((a, b) => a[0].localeCompare(b[0], "es"));
}

/** Una sola fila de catálogo para series / anime (sin partir por género). */
function groupSeriesCatalog(items, { animeOnly = false } = {}) {
  const shows = buildSeriesShows(items, { includeAnime: animeOnly }).filter((show) =>
    animeOnly ? (show.content_type || "") === "anime_series" : (show.content_type || "") !== "anime_series"
  );
  const catalog = shows.map(showToCatalogItem);
  if (!catalog.length) return [];
  const label = animeOnly ? "Anime" : "Series";
  return [[label, catalog]];
}

const SECTIONS = {
  home: {
    title: "Inicio",
    eyebrow: "Tu catálogo",
    empty: "No hay vídeos listos todavía. Publícalos con Casasm Desktop.",
  },
  movie: {
    title: "Películas",
    eyebrow: "Cine en casa",
    empty: "Aún no hay películas publicadas.",
  },
  series: {
    title: "Series",
    eyebrow: "Temporadas y episodios",
    empty: "Aún no hay series publicadas.",
  },
  anime: {
    title: "Anime",
    eyebrow: "Series japonesas",
    empty: "Aún no hay series de anime publicadas.",
  },
  favorites: {
    title: "Favoritos",
    eyebrow: "Tu lista",
    empty: "Aún no tienes favoritos. Pulsa la estrella en cualquier título.",
  },
};

const TYPE_LABEL = {
  movie: "Película",
  series: "Serie",
  anime: "Película · Anime",
  anime_series: "Anime (serie)",
};

const ANIME_TYPES = new Set(["anime_series"]);


function PosterCard({
  video,
  onNavigate,
  rank = null,
  featured = false,
  index = 0,
  showProgress = false,
}) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [favBusy, setFavBusy] = useState(false);
  const rating = video.rating_avg;
  const favored = isFavorite(video.id) || Boolean(video.is_favorite);
  const progress = Math.max(0, Math.min(100, Number(video.progress_percent) || 0));

  async function onFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (favBusy) return;
    setFavBusy(true);
    try {
      await toggleFavorite(video.id);
    } catch (_) {
      /* ignore */
    } finally {
      setFavBusy(false);
    }
  }

  const posterSrc =
    video.cover_url ||
    video.poster_url ||
    video.series_poster_url ||
    video.season_poster_url ||
    video.backdrop_url ||
    video.series_backdrop_url ||
    "";

  const media = (
    <div className="poster-media">
      {posterSrc ? (
        <img
          src={posterSrc}
          alt={video.title}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      ) : (
        <div className="poster-fallback">{(video.title || "?").slice(0, 1)}</div>
      )}
      {!featured && rank != null ? (
        <span className={`poster-rank ${rank <= 3 ? `rank-${rank}` : ""}`}>#{rank}</span>
      ) : null}
      {rating ? (
        <span className="poster-rating" title={`${rating} / 5`}>
          <span className="poster-rating-star" aria-hidden="true">
            ★
          </span>
          <span className="poster-rating-value">{rating}</span>
        </span>
      ) : null}
      {featured && (video.view_count || 0) > 0 ? (
        <span className="poster-views">{video.view_count} vistas</span>
      ) : null}
      <button
        type="button"
        className={`poster-fav ${favored ? "is-on" : ""}`}
        aria-label={favored ? "Quitar de favoritos" : "Añadir a favoritos"}
        aria-pressed={favored}
        disabled={favBusy}
        onClick={onFavoriteClick}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M12 3.6 14.4 9l5.8.5-4.4 3.8 1.4 5.6L12 16.2 6.8 18.9l1.4-5.6L3.8 9.5 9.6 9 12 3.6z"
            fill={favored ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="poster-hover">
        <span className="play-dot" aria-hidden="true" />
        <span className="poster-hover-title">{video.title}</span>
      </div>
      {showProgress && progress > 0 ? (
        <div className="poster-progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      ) : null}
    </div>
  );

  return (
    <Link
      to={video.catalog_href || `/watch/${video.id}`}
      className={`poster ${featured ? "poster-featured" : ""}`}
      style={{ "--poster-i": index }}
      draggable={false}
      onClick={(e) => {
        if (onNavigate && !onNavigate()) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {featured && rank != null ? (
        <span className={`poster-rank-num ${rank <= 3 ? `rank-${rank}` : ""}`} aria-hidden="true">
          {rank}
        </span>
      ) : null}
      {featured ? (
        <div className="poster-featured-body">
          {media}
          <span className="poster-caption">{video.title}</span>
        </div>
      ) : (
        <>
          {media}
          <span className="poster-caption">{video.title}</span>
        </>
      )}
    </Link>
  );
}

function ContentRow({
  title,
  items,
  linkTo,
  featured = false,
  variant = "",
  eyebrow = "",
  rowId = "",
  eager: _eager = false,
  toneIndex = null,
  showProgress = false,
  limit = null,
  moreLabel = "Ver más",
}) {
  const sectionRef = useRef(null);
  const rowRef = useRef(null);
  const dragState = useRef({
    active: false,
    dragging: false,
    startX: 0,
    startScroll: 0,
    pointerId: null,
    suppressClick: false,
  });
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [dragging, setDragging] = useState(false);

  const visibleItems = useMemo(() => {
    if (limit == null || limit <= 0) return items;
    return items.slice(0, limit);
  }, [items, limit]);

  function updateArrows() {
    const el = rowRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(max > 4 && el.scrollLeft < max - 4);
  }

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;

    updateArrows();
    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateArrows);

    const images = el.querySelectorAll("img");
    images.forEach((img) => {
      if (!img.complete) img.addEventListener("load", updateArrows);
    });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateArrows());
      ro.observe(el);
    }

    const timers = [50, 250, 800].map((ms) => window.setTimeout(updateArrows, ms));

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateArrows);
      images.forEach((img) => img.removeEventListener("load", updateArrows));
      if (ro) ro.disconnect();
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [visibleItems]);

  function scrollByDir(dir) {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: "smooth" });
  }

  function allowPosterClick() {
    if (dragState.current.suppressClick) {
      dragState.current.suppressClick = false;
      return false;
    }
    return true;
  }

  function onPointerDown(e) {
    // En táctil dejamos el scroll nativo horizontal
    if (e.pointerType === "touch") return;
    if (e.button !== 0) return;
    const el = rowRef.current;
    if (!el) return;
    dragState.current = {
      active: true,
      dragging: false,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      pointerId: e.pointerId,
      suppressClick: false,
    };
  }

  function onPointerMove(e) {
    const state = dragState.current;
    const el = rowRef.current;
    if (!state.active || !el) return;
    const dx = e.clientX - state.startX;
    // Umbral alto: un click normal no debe contar como arrastre.
    if (!state.dragging && Math.abs(dx) < 14) return;

    if (!state.dragging) {
      state.dragging = true;
      state.suppressClick = true;
      setDragging(true);
      el.setPointerCapture?.(e.pointerId);
    }
    el.scrollLeft = state.startScroll - dx;
    updateArrows();
  }

  function endDrag(e) {
    const state = dragState.current;
    const el = rowRef.current;
    if (!state.active) return;
    const wasDragging = state.dragging;
    state.active = false;
    state.dragging = false;
    setDragging(false);
    if (el && wasDragging && state.pointerId != null) {
      try {
        el.releasePointerCapture?.(state.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    // Tras un drag, el click sintético llega justo después: mantener el bloqueo un frame.
    if (wasDragging) {
      state.suppressClick = true;
      window.setTimeout(() => {
        dragState.current.suppressClick = false;
      }, 120);
    }
    if (e?.pointerType === "mouse" && wasDragging) {
      e.preventDefault?.();
    }
  }

  if (!items.length) return null;

  const sectionClass = [
    "row-section",
    featured ? "row-section-featured" : "",
    variant === "new" ? "row-section-new" : "",
    toneIndex != null ? `row-tone-${(toneIndex % 6) + 1}` : "",
    "is-inview",
  ]
    .filter(Boolean)
    .join(" ");
  const headClass = [
    "row-head",
    featured ? "row-head-featured" : "",
    variant === "new" ? "row-head-new" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const rowClass = [
    "poster-row",
    featured ? "poster-row-featured" : "",
    variant === "new" ? "poster-row-new" : "",
    dragging ? "is-dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showMore =
    Boolean(linkTo) &&
    (limit == null ? items.length > 0 : items.length > limit || items.length > 0);

  return (
    <section ref={sectionRef} id={rowId || undefined} className={sectionClass}>
      <div className={headClass}>
        <div>
          {eyebrow ? <p className="row-eyebrow">{eyebrow}</p> : null}
          <h2>{title}</h2>
        </div>
        {showMore ? (
          <Link to={linkTo} className="row-more">
            {moreLabel}
          </Link>
        ) : null}
      </div>
      <div className="row-slider">
        <button
          type="button"
          className={`row-arrow row-arrow-left ${canLeft ? "visible" : ""}`}
          aria-label="Anterior"
          disabled={!canLeft}
          onClick={() => scrollByDir(-1)}
        >
          ‹
        </button>
        <div
          className={rowClass}
          ref={rowRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={(e) => {
            if (dragState.current.active && dragState.current.dragging) endDrag(e);
          }}
        >
          {visibleItems.map((video, i) => (
            <PosterCard
              key={video.catalog_key || video.id}
              video={video}
              onNavigate={allowPosterClick}
              featured={featured}
              rank={featured ? i + 1 : null}
              index={i}
              showProgress={showProgress}
            />
          ))}
        </div>
        <button
          type="button"
          className={`row-arrow row-arrow-right ${canRight ? "visible" : ""}`}
          aria-label="Siguiente"
          disabled={!canRight}
          onClick={() => scrollByDir(1)}
        >
          ›
        </button>
      </div>
    </section>
  );
}

function HeroCarousel({ items }) {
  const [index, setIndex] = useState(0);
  const count = items.length;
  const current = items[index] || null;

  useEffect(() => {
    setIndex(0);
  }, [items]);

  useEffect(() => {
    if (count < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [count, index]);

  if (!current) return null;

  function go(next) {
    if (!count) return;
    setIndex(((next % count) + count) % count);
  }

  return (
    <section
      className="hero-billboard"
      style={
        current.backdrop_url || current.cover_url
          ? {
              "--hero-image": `url(${current.backdrop_url || current.cover_url})`,
            }
          : undefined
      }
    >
      <div className="hero-billboard-bg" key={current.id} aria-hidden="true" />
      <div className="hero-billboard-fade" aria-hidden="true" />
      <div className="hero-billboard-content">
        <p className="hero-kicker">
          {`${current.isNew ? "Nuevo · " : ""}${TYPE_LABEL[current.content_type] || "Destacado"}${
            current.category ? ` · ${current.category}` : ""
          }`}
        </p>
        <h1>{current.title}</h1>
        {current.description ? (
          <p className="hero-synopsis">{current.description}</p>
        ) : (
          <p className="hero-synopsis muted">Listo para reproducir en tu red local.</p>
        )}
        <div className="hero-actions">
          <Link to={current.playHref || current.href || `/watch/${current.id}`} className="btn-play">
            Reproducir
          </Link>
          <Link to={current.href || `/watch/${current.id}`} className="btn-info">
            Más info
          </Link>
        </div>
        {count > 1 ? (
          <div className="hero-thumbs" role="tablist" aria-label="Últimos añadidos">
            {items.slice(0, 8).map((item, i) => {
              const thumb = item.cover_url || item.backdrop_url;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  className={`hero-thumb ${i === index ? "active" : ""}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Ir a ${item.title}`}
                  style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                />
              );
            })}
          </div>
        ) : null}
      </div>
      {count > 1 ? (
        <>
          <button
            type="button"
            className="hero-nav hero-nav-left"
            aria-label="Anterior destacado"
            onClick={() => go(index - 1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="hero-nav hero-nav-right"
            aria-label="Siguiente destacado"
            onClick={() => go(index + 1)}
          >
            ›
          </button>
        </>
      ) : null}
    </section>
  );
}

function SectionExplore({
  title,
  items,
  categories,
  activeCategory,
  onCategory,
  decade,
  decades,
  onDecade,
  onSurprise,
}) {
  const slides = useMemo(() => {
    // Prioriza backdrops horizontales; el póster vertical se ve mal y corta caras.
    const backdrops = items.filter((v) => v.backdrop_url);
    if (backdrops.length) return backdrops;
    return items.filter((v) => v.cover_url);
  }, [items]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 9000);
    return () => window.clearInterval(timer);
  }, [slides]);

  return (
    <section className="section-explore">
      <div className="section-banner-slides" aria-hidden="true">
        {slides.map((video, i) => {
          const art = video.backdrop_url || video.cover_url;
          return (
            <div
              key={video.id}
              className={`section-banner-slide ${i === index ? "active" : ""}`}
            >
              {art ? <img src={art} alt="" decoding="async" /> : null}
            </div>
          );
        })}
        <div className="section-banner-shade" />
      </div>

      <div className="section-explore-panel">
        <h1 className="section-banner-title">{title}</h1>

        <div className="section-explore-tools">
          <button type="button" className="section-surprise" onClick={onSurprise}>
            Sorpréndeme
          </button>
          {decades.length ? (
            <div className="section-filter-group" aria-label="Décadas">
              <button
                type="button"
                className={`section-chip ${decade == null ? "active" : ""}`}
                onClick={() => onDecade(null)}
              >
                Todas
              </button>
              {decades.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`section-chip ${decade === d ? "active" : ""}`}
                  onClick={() => onDecade(d)}
                >
                  {d}s
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {categories.length > 1 ? (
          <div className="section-chip-bar embedded" aria-label="Categorías">
            <button
              type="button"
              className={`section-chip ${!activeCategory ? "active" : ""}`}
              onClick={() => onCategory(null)}
            >
              Todo
            </button>
            {categories.map(([category]) => (
              <button
                key={category}
                type="button"
                className={`section-chip ${activeCategory === category ? "active" : ""}`}
                title={category}
                onClick={() => onCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function CatalogPage({ section = "home" }) {
  const { token, profile } = useAuth();
  const { favoriteIds } = useFavorites();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [recentlyWatched, setRecentlyWatched] = useState([]);
  const q = searchParams.get("q") || "";
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [decade, setDecade] = useState(null);
  const [sagaRules, setSagaRules] = useState([]);
  const meta = SECTIONS[section] || SECTIONS.home;

  useEffect(() => {
    setActiveCategory(null);
    setDecade(null);
  }, [section, q]);

  useEffect(() => {
    let cancelled = false;
    api("/api/sagas/rules")
      .then((data) => {
        if (!cancelled) setSagaRules(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setSagaRules([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("/api/videos", { token })
      .then((data) => {
        if (!cancelled) setVideos(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id]);

  useEffect(() => {
    if (!token || !profile?.id || section !== "home") {
      setRecentlyWatched([]);
      return undefined;
    }
    let cancelled = false;
    api("/api/recently-watched", { token })
      .then((data) => {
        if (!cancelled) setRecentlyWatched(data.videos || []);
      })
      .catch(() => {
        if (!cancelled) setRecentlyWatched([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id, section]);

  const scoped = useMemo(() => {
    if (section === "home") return videos;
    if (section === "favorites") {
      return videos.filter((v) => favoriteIds.has(v.id) || v.is_favorite);
    }
    if (section === "anime") {
      return videos.filter((v) => ANIME_TYPES.has(v.content_type || ""));
    }
    if (section === "movie") {
      // Películas + residual content_type anime (película).
      return videos.filter((v) => {
        const t = v.content_type || "movie";
        return t === "movie" || t === "anime";
      });
    }
    return videos.filter((v) => (v.content_type || "movie") === section);
  }, [videos, section, favoriteIds]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return scoped;
    return scoped.filter((v) => {
      const hay = [
        v.title,
        v.series_title,
        v.category,
        ...(videoCategories(v) || []),
        TYPE_LABEL[v.content_type] || "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [scoped, q]);

  const carouselItems = useMemo(() => {
    if (section !== "home") return [];
    // Carrusel = últimos subidos (películas + series/anime agregados).
    const shows = buildSeriesShows(filtered);
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const seriesBanners = shows.map((show) => {
      const sample =
        show.items.find((v) => v.series_backdrop_url) ||
        show.items.find((v) => v.backdrop_url) ||
        show.items.find((v) => v.cover_url || v.series_poster_url) ||
        show.items[0];
      const firstEp = show.items[0];
      const newestTs = Math.max(
        ...show.items.map((v) => Date.parse(v.created_at || "") || Number(v.id) || 0),
        0
      );
      const seriesOverview = [...show.items]
        .map((v) => (v.description || "").trim())
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)[0];
      return {
        id: `series:${show.slug}`,
        title: show.title,
        description:
          seriesOverview ||
          `${show.count} capítulo${show.count === 1 ? "" : "s"} · ${show.seasonCount} temporada${
            show.seasonCount === 1 ? "" : "s"
          }`,
        cover_url: show.cover_url || sample?.series_poster_url || sample?.cover_url,
        backdrop_url:
          sample?.series_backdrop_url || show.backdrop_url || sample?.backdrop_url || show.cover_url,
        content_type: show.content_type || "series",
        category: sample?.category || "",
        href: `/series/${show.slug}`,
        playHref: firstEp ? `/watch/${firstEp.id}` : `/series/${show.slug}`,
        _sort: newestTs,
        isNew: newestTs >= weekAgo,
      };
    });

    const movies = filtered
      .filter((v) => !isSeriesVideo(v))
      .map((v) => {
        const ts = Date.parse(v.created_at || "") || Number(v.id) || 0;
        return {
          ...v,
          href: `/watch/${v.id}`,
          playHref: `/watch/${v.id}`,
          _sort: ts,
          isNew: ts >= weekAgo,
        };
      });

    const pool = [...seriesBanners, ...movies]
      .filter((v) => v.backdrop_url || v.cover_url)
      .sort((a, b) => (b._sort || 0) - (a._sort || 0));
    const fallback = [...seriesBanners, ...movies].sort(
      (a, b) => (b._sort || 0) - (a._sort || 0)
    );
    return (pool.length ? pool : fallback).slice(0, 11);
  }, [filtered, section]);

  const newestUploads = useMemo(() => {
    const shows = buildSeriesShows(filtered).map((show) => {
      const newestTs = Math.max(
        ...show.items.map((v) => Date.parse(v.created_at || "") || Number(v.id) || 0),
        0
      );
      const sample =
        show.items.find((v) => v.series_poster_url || v.cover_url) || show.items[0];
      return {
        id: sample?.id || show.items[0]?.id,
        catalog_key: `new-series:${show.slug}`,
        catalog_href: `/series/${show.slug}`,
        title: show.title,
        cover_url: show.cover_url || sample?.series_poster_url || sample?.cover_url,
        series_poster_url: sample?.series_poster_url,
        content_type: show.content_type || "series",
        created_at: new Date(newestTs).toISOString(),
        _sort: newestTs,
      };
    });
    const movies = filtered
      .filter((v) => !isSeriesVideo(v))
      .map((v) => ({
        ...v,
        catalog_href: `/watch/${v.id}`,
        _sort: Date.parse(v.created_at || "") || Number(v.id) || 0,
      }));
    return [...shows, ...movies]
      .sort((a, b) => (b._sort || 0) - (a._sort || 0))
      .slice(0, 18);
  }, [filtered]);

  const topViewed = useMemo(() => {
    // Ranking global de la casa: todos los perfiles/usuarios.
    return [...scoped]
      .sort(
        (a, b) =>
          (b.view_count || 0) - (a.view_count || 0) ||
          (b.rating_avg || 0) - (a.rating_avg || 0) ||
          String(a.title).localeCompare(String(b.title), "es")
      )
      .slice(0, 5);
  }, [scoped]);

  const byType = useMemo(() => {
    const groups = { movie: [], series: [], anime_series: [] };
    filtered.forEach((v) => {
      let key = v.content_type || "movie";
      if (key === "anime") key = "movie"; // película anime → fila de películas
      if (groups[key]) groups[key].push(v);
      else groups.movie.push(v);
    });
    Object.keys(groups).forEach((key) => {
      groups[key] = [...groups[key]].sort((a, b) => (b.id || 0) - (a.id || 0));
    });
    return groups;
  }, [filtered]);

  const byCategory = useMemo(() => {
    if (section === "favorites") {
      return filtered.length ? [["Mis favoritos", filtered]] : [];
    }

    if (section === "series") {
      return groupSeriesCatalog(filtered, { animeOnly: false });
    }

    if (section === "anime") {
      return groupSeriesCatalog(filtered, { animeOnly: true });
    }

    return groupByGenre(filtered);
  }, [filtered, section]);

  const decades = useMemo(() => {
    const set = new Set();
    const pool =
      section === "series" || section === "anime"
        ? byCategory.flatMap(([, items]) => items)
        : filtered;
    pool.forEach((v) => {
      const y = Number(v.year);
      // Incluye clásicos (Bond 1964, etc.); antes solo ≥1970 y no salían en chips.
      if (y >= 1950 && y <= 2035) set.add(Math.floor(y / 10) * 10);
    });
    return [...set].sort((a, b) => b - a);
  }, [filtered, section, byCategory]);

  const exploreItems = useMemo(() => {
    if (section === "series" || section === "anime") {
      let list = byCategory.flatMap(([, items]) => items);
      if (activeCategory) {
        const match = byCategory.find(([name]) => name === activeCategory);
        list = match ? match[1] : [];
      }
      if (decade != null) {
        list = list.filter((v) => {
          const y = Number(v.year);
          return y >= decade && y < decade + 10;
        });
      }
      return list;
    }

    let list = filtered;
    if (decade != null) {
      list = list.filter((v) => {
        const y = Number(v.year);
        return y >= decade && y < decade + 10;
      });
    }
    if (activeCategory) {
      list = list.filter((v) => videoInCategory(v, activeCategory));
    }
    return list;
  }, [filtered, decade, activeCategory, section, byCategory]);

  const displayCategories = useMemo(() => {
    if (section === "home") return [];
    if (activeCategory) {
      const match = byCategory.find(([name]) => name === activeCategory);
      if (!match) return [];
      const items = match[1].filter((v) =>
        exploreItems.some(
          (e) => (e.catalog_key || e.id) === (v.catalog_key || v.id)
        )
      );
      return items.length ? [[match[0], items]] : [];
    }
    if (decade == null) return byCategory;
    return byCategory
      .map(([name, items]) => [
        name,
        items.filter((v) =>
          exploreItems.some(
            (e) => (e.catalog_key || e.id) === (v.catalog_key || v.id)
          )
        ),
      ])
      .filter(([, items]) => items.length);
  }, [section, byCategory, activeCategory, decade, exploreItems]);

  function categoryId(name) {
    return `cat-${String(name)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")}`;
  }

  function surpriseMe() {
    const pool = exploreItems.length ? exploreItems : filtered;
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    navigate(pick.catalog_href || `/watch/${pick.id}`);
  }

  return (
    <Shell wide>
      {section === "home" && carouselItems.length ? (
        <HeroCarousel items={carouselItems} />
      ) : section === "home" ? (
        <section className="hero-empty">
          <p className="muted">{meta.eyebrow}</p>
        </section>
      ) : !loading && filtered.length ? (
        <SectionExplore
          title={meta.title}
          items={filtered}
          categories={byCategory}
          activeCategory={activeCategory}
          onCategory={setActiveCategory}
          decade={decade}
          decades={decades}
          onDecade={setDecade}
          onSurprise={surpriseMe}
        />
      ) : null}

      {loading ? (
        <p
          className={`muted catalog-status ${
            section !== "home" ? "catalog-status-section" : ""
          }`}
        >
          Cargando catálogo…
        </p>
      ) : null}
      {error ? (
        <p
          className={`error catalog-status ${
            section !== "home" ? "catalog-status-section" : ""
          }`}
        >
          {error}
        </p>
      ) : null}
      {!loading && !filtered.length ? (
        <p
          className={`muted catalog-status ${
            section !== "home" ? "catalog-status-section" : ""
          }`}
        >
          {meta.empty}
        </p>
      ) : null}

      <div
        className={`catalog-rows ${
          section === "home" ? "catalog-rows-home" : "catalog-rows-section with-explore"
        } ${q.trim() ? "search-soft" : ""}`}
        key={`rows-${section}-${q}`}
      >
        {section === "home" ? (
          <>
            <ContentRow
              title="Recién añadidos"
              items={newestUploads}
              eager
              limit={12}
              variant="new"
            />
            {recentlyWatched.length ? (
              <ContentRow
                title="Continuar viendo"
                items={recentlyWatched}
                showProgress
                eager
                variant="continue"
                limit={12}
              />
            ) : null}
            <ContentRow
              title="Top 5 más vistas"
              items={topViewed}
              featured
              eager
            />
            <SagasRow videos={filtered} customRules={sagaRules} />
            <SeriesRow videos={filtered} />
            <ContentRow
              title="Películas nuevas"
              items={byType.movie}
              linkTo="/peliculas"
              moreLabel="Ver más"
              limit={9}
              variant="new"
            />
            <AnimeSeriesRow videos={filtered} />
          </>
        ) : section === "favorites" ? (
          <>
            {filtered.length ? (
              <ContentRow title="Mis favoritos" items={filtered} eager />
            ) : null}
          </>
        ) : (
          <>
            {!displayCategories.length && filtered.length ? (
              <p className="muted catalog-status">No hay títulos con ese filtro.</p>
            ) : null}
            {displayCategories.map(([category, items], index) => {
              const moreTo =
                section === "movie"
                  ? `/peliculas/${slugifyGenre(category)}`
                  : section === "anime"
                    ? `/anime/${slugifyGenre(category)}`
                    : null;
              const isSeriesSection = section === "series" || section === "anime";
              return (
                <ContentRow
                  key={category}
                  title={isSeriesSection ? meta.title : category}
                  items={items}
                  rowId={categoryId(category)}
                  eager={index < 6}
                  toneIndex={index}
                  limit={isSeriesSection ? null : 9}
                  moreLabel="Ver más"
                  linkTo={moreTo}
                />
              );
            })}
          </>
        )}
      </div>
    </Shell>
  );
}
